import React, { useRef, useState } from 'react'
import { Box, Stack } from '@mui/material'
import dayjs from 'dayjs'

import MultiDatePicker from '~components/common/MultiDatePicker'
import { Button, ButtonTabs, Icon, IconButton, Typography } from '~components/internal'
import { chartColors, spacing } from '~tokens/internal'
import { DAYS_OF_WEEK, type EnrichedAIPhoneLineConfig } from '~types'
import { getUserTimezone } from '~utils/dateUtils'

import AIPhoneLineConfigCard from './AIPhoneLineConfigCard'
import AIPhoneLineHolidayCard from './AIPhoneLineHolidayCard'
import WeekView from './WeekView'

type MoveDirection = 'up' | 'down'

const withDescendingPriority = (
  configs: EnrichedAIPhoneLineConfig[],
): EnrichedAIPhoneLineConfig[] =>
  configs.map((config, i) => ({ ...config, priority: configs.length - 1 - i }))

interface SummaryRowProps {
  label: string
  detail: string
  color?: string
  priority?: number
  onEdit: () => void
  onDelete: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
}

const SummaryRow: React.FC<SummaryRowProps> = ({
  label,
  detail,
  color,
  priority,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
}) => (
  <Box
    onClick={onEdit}
    className='summary-row'
    style={{ '--summary-row-color': color } as React.CSSProperties}
  >
    <Box>
      <Box className='summary-row__label-row'>
        <Typography>{label}</Typography>
        {priority !== undefined && (
          <Box className='priority-badge' sx={{ typography: 'caption' }}>
            Priority {priority}
          </Box>
        )}
      </Box>
      <Typography variant='caption'>{detail}</Typography>
    </Box>
    <Box className='summary-row__actions' onClick={(e) => e.stopPropagation()}>
      {onMoveUp && (
        <IconButton size='small' onClick={onMoveUp}>
          <Icon name='chevronUp' />
        </IconButton>
      )}
      {onMoveDown && (
        <IconButton size='small' onClick={onMoveDown}>
          <Icon name='chevronDown' />
        </IconButton>
      )}
      <IconButton size='small' color='error' onClick={onDelete}>
        <Icon name='delete' />
      </IconButton>
    </Box>
  </Box>
)

interface ConfigTabPanelProps {
  editIndex: number | null
  setEditIndex: (i: number | null) => void
  items: EnrichedAIPhoneLineConfig[]
  onAdd: () => void
  onDelete: (i: number) => void
  onMove?: (i: number, dir: MoveDirection) => void
  addLabel: string
  emptyText: string
  description: string
  renderCard: (item: EnrichedAIPhoneLineConfig, index: number) => React.ReactNode
  getSummaryLabel: (item: EnrichedAIPhoneLineConfig, index: number) => string
  getSummaryDetail: (item: EnrichedAIPhoneLineConfig, index: number) => string
  getSummaryColor?: (item: EnrichedAIPhoneLineConfig, index: number) => string | undefined
  getSummaryPriority?: (item: EnrichedAIPhoneLineConfig, index: number) => number | undefined
  extraContent?: React.ReactNode
}

const ConfigTabPanel: React.FC<ConfigTabPanelProps> = ({
  editIndex,
  setEditIndex,
  items,
  onAdd,
  onDelete,
  onMove,
  addLabel,
  emptyText,
  description,
  renderCard,
  getSummaryLabel,
  getSummaryDetail,
  getSummaryColor,
  getSummaryPriority,
  extraContent,
}) => {
  const editItem = editIndex !== null ? (items[editIndex] ?? null) : null

  if (editItem !== null && editIndex !== null) {
    return (
      <Stack spacing={spacing.xs}>
        {renderCard(editItem, editIndex)}
        <Button variant='contained' size='small' onClick={() => setEditIndex(null)}>
          Done
        </Button>
      </Stack>
    )
  }

  return (
    <Stack spacing={spacing.xs}>
      <Box className='config-tab-panel__header'>
        <Typography>{description}</Typography>
        <Button startIcon={<Icon name='add' />} onClick={onAdd} variant='outlined' size='small'>
          {addLabel}
        </Button>
      </Box>
      {extraContent}
      {items.length === 0 ? (
        <Typography align='center'>{emptyText}</Typography>
      ) : (
        items.map((item, i) => (
          <SummaryRow
            key={i}
            label={getSummaryLabel(item, i)}
            detail={getSummaryDetail(item, i)}
            color={getSummaryColor?.(item, i)}
            priority={getSummaryPriority?.(item, i)}
            onEdit={() => setEditIndex(i)}
            onDelete={() => onDelete(i)}
            onMoveUp={onMove && i > 0 ? (): void => onMove(i, 'up') : undefined}
            onMoveDown={onMove && i < items.length - 1 ? (): void => onMove(i, 'down') : undefined}
          />
        ))
      )}
    </Stack>
  )
}

export interface ScheduleSettingsStepProps {
  phoneLineConfigurations: EnrichedAIPhoneLineConfig[]
  setPhoneLineConfigurations: (configs: EnrichedAIPhoneLineConfig[]) => void
  phoneLineHolidays: EnrichedAIPhoneLineConfig[]
  setPhoneLineHolidays: (holidays: EnrichedAIPhoneLineConfig[]) => void
}

const ScheduleSettingsStep: React.FC<ScheduleSettingsStepProps> = ({
  phoneLineConfigurations,
  setPhoneLineConfigurations,
  phoneLineHolidays,
  setPhoneLineHolidays,
}) => {
  const defaultTimezone = getUserTimezone('America/New_York')

  const colorIndexMap = useRef(new Map<string, number>())
  phoneLineConfigurations.forEach((config) => {
    if (!colorIndexMap.current.has(config.id)) {
      colorIndexMap.current.set(config.id, colorIndexMap.current.size)
    }
  })
  const colorOf = (config: EnrichedAIPhoneLineConfig): string =>
    chartColors[colorIndexMap.current.get(config.id)! % chartColors.length]

  const [tab, setTab] = useState<'time' | 'holiday'>('time')
  const [editTime, setEditTime] = useState<number | null>(null)
  const [editHoliday, setEditHoliday] = useState<number | null>(null)

  const addConfiguration = (): void => {
    const tempUUID = crypto.randomUUID()
    const newConfig: EnrichedAIPhoneLineConfig = {
      id: tempUUID,
      phone_line: tempUUID,
      name: `Configuration ${phoneLineConfigurations.length + 1}`,
      greeting_message: '',
      default_transfer_number: '',
      default_transfer_extension: undefined,
      start_time: '09:00',
      end_time: '17:00',
      timezone: defaultTimezone,
      days_of_week: [1, 2, 3, 4, 5],
      priority: 0,
    }
    setPhoneLineConfigurations(withDescendingPriority([newConfig, ...phoneLineConfigurations]))
    setEditTime(0)
  }

  const updateConfiguration = (index: number, config: EnrichedAIPhoneLineConfig): void => {
    const newConfigurations = [...phoneLineConfigurations]
    newConfigurations[index] = config
    setPhoneLineConfigurations(newConfigurations)
  }

  const deleteConfiguration = (index: number): void => {
    setPhoneLineConfigurations(
      withDescendingPriority(phoneLineConfigurations.filter((_, i) => i !== index)),
    )
  }

  const moveConfiguration = (index: number, direction: MoveDirection): void => {
    const newConfigurations = [...phoneLineConfigurations]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex < 0 || targetIndex >= newConfigurations.length) return
    const temp = newConfigurations[index]
    newConfigurations[index] = newConfigurations[targetIndex]
    newConfigurations[targetIndex] = temp
    setPhoneLineConfigurations(withDescendingPriority(newConfigurations))
  }

  const addHoliday = (): void => {
    const tempUUID = crypto.randomUUID()
    const newHoliday: EnrichedAIPhoneLineConfig = {
      id: tempUUID,
      phone_line: tempUUID,
      name: `Holiday ${phoneLineHolidays.length + 1}`,
      greeting_message: '',
      default_transfer_number: '',
      default_transfer_extension: undefined,
      start_time: '00:00:00',
      end_time: '23:59:59',
      timezone: defaultTimezone,
      days_of_week: [],
      priority: 9,
      inactive_dates: [],
      applied_to_transfer_lines: true,
    }
    setPhoneLineHolidays([newHoliday, ...phoneLineHolidays])
    setEditHoliday(0)
  }

  const updateHoliday = (index: number, config: EnrichedAIPhoneLineConfig): void => {
    const newHolidays = [...phoneLineHolidays]
    newHolidays[index] = config
    setPhoneLineHolidays(newHolidays)
  }

  const deleteHoliday = (index: number): void => {
    setPhoneLineHolidays(withDescendingPriority(phoneLineHolidays.filter((_, i) => i !== index)))
  }

  return (
    <Stack spacing={1}>
      <ButtonTabs
        options={[
          { label: 'Time-Based', value: 'time' },
          { label: 'Holiday-Based', value: 'holiday' },
        ]}
        value={tab}
        onChange={(v) => {
          setTab(v as 'time' | 'holiday')
          setEditTime(null)
          setEditHoliday(null)
        }}
      />
      {tab === 'time' && (
        <ConfigTabPanel
          editIndex={editTime}
          setEditIndex={setEditTime}
          items={phoneLineConfigurations}
          onAdd={addConfiguration}
          onDelete={deleteConfiguration}
          onMove={moveConfiguration}
          addLabel='Add Configuration'
          emptyText='No time-based configurations yet.'
          description='Click a block or row to edit. Overlaps resolve by priority order.'
          renderCard={(config, index) => (
            <AIPhoneLineConfigCard
              config={config}
              index={index}
              onUpdate={updateConfiguration}
              onDelete={(i) => {
                deleteConfiguration(i)
                setEditTime(null)
              }}
            />
          )}
          getSummaryLabel={(config, i) => config.name || `Configuration ${i + 1}`}
          getSummaryPriority={(_, i) => i}
          getSummaryDetail={(config) => {
            const days = DAYS_OF_WEEK.filter((d) => config.days_of_week?.includes(d.value))
              .map((d) => d.label)
              .join(', ')
            return `${days} · ${(config.start_time || '').slice(0, 5)} - ${(config.end_time || '').slice(0, 5)}`
          }}
          getSummaryColor={colorOf}
          extraContent={
            <WeekView configs={phoneLineConfigurations} colorOf={colorOf} onClick={setEditTime} />
          }
        />
      )}
      {tab === 'holiday' && (
        <ConfigTabPanel
          editIndex={editHoliday}
          setEditIndex={setEditHoliday}
          items={phoneLineHolidays}
          onAdd={addHoliday}
          onDelete={deleteHoliday}
          addLabel='Add Holiday'
          emptyText='No holiday configurations yet.'
          description='Click a row or highlighted date to edit.'
          renderCard={(config, index) => (
            <AIPhoneLineHolidayCard
              config={config}
              index={index}
              onUpdate={updateHoliday}
              onDelete={(i) => {
                deleteHoliday(i)
                setEditHoliday(null)
              }}
            />
          )}
          getSummaryLabel={(h, i) => h.name || `Holiday ${i + 1}`}
          getSummaryDetail={(h) =>
            h.inactive_dates?.map((d) => dayjs(d).format('MMM D, YYYY')).join(', ') ?? ''
          }
          extraContent={
            <MultiDatePicker
              selectedDays={phoneLineHolidays.flatMap(
                (h) => h.inactive_dates?.map((d) => dayjs(d)) || [],
              )}
              setSelectedDays={(selectedDays) => {
                const selectedDates = new Set(selectedDays.map((d) => d.format('YYYY-MM-DD')))
                const holidayIndex = phoneLineHolidays.findIndex((h) =>
                  h.inactive_dates?.some((d) => !selectedDates.has(d)),
                )
                if (holidayIndex !== -1) setEditHoliday(holidayIndex)
              }}
            />
          }
        />
      )}
    </Stack>
  )
}

export default ScheduleSettingsStep
